import { Button, Drawer, Spin } from 'antd';
import React, { useState, useCallback, useEffect } from 'react';
import { getPracticeOverviewAttention, getPracticeOverviewAttentionDetail, dismissPracticeOverviewAttention } from 'api/group';
import {
    TPracticeAttentionAlertVo,
    TPracticeAttentionDetailVo,
    TAttentionAlertType,
} from '../../types';
import s from './s.module.less';

const ErrorIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" width="16" height="16">
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8ZM9 12C9 12.5523 8.55229 13 8 13C7.44772 13 7 12.5523 7 12C7 11.4477 7.44772 11 8 11C8.55229 11 9 11.4477 9 12ZM8 3C7.44772 3 7 3.44772 7 4V8C7 8.55228 7.44772 9 8 9C8.55229 9 9 8.55228 9 8V4C9 3.44772 8.55229 3 8 3Z"
            fill="#DC2626"
        />
    </svg>
);

const WarningIcon = () => (
    <svg viewBox="0 0 17 15" fill="none" width="17" height="15">
        <path
            d="M8.27723 5.83333V7.5M8.27723 10.8333H8.28557M2.50373 14.1667H14.0507C15.3337 14.1667 16.1356 12.7778 15.4941 11.6667L9.72061 1.66667C9.07911 0.555556 7.47536 0.555555 6.83386 1.66667L1.06035 11.6667C0.418854 12.7778 1.22073 14.1667 2.50373 14.1667Z"
            stroke="#D97706"
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const ALERT_LABELS: Record<string, string> = {
    FAILED_CHARGES: 'recent charges could not be processed because the patient\'s card on file failed.',
    PAST_DUE_INVOICES: 'invoices are past due.',
    INCOMPLETE_ONBOARDING: 'team members have not completed onboarding steps.',
    EXPIRING_LICENSES: 'providers have licenses expiring within 30 days.',
    HIGH_CANCEL_RATE: 'The cancellation rate is higher than your typical range.',
};

const ALERT_TITLE: Record<string, string> = {
    FAILED_CHARGES: 'Failed Charges',
    PAST_DUE_INVOICES: 'Past Due Invoices',
    INCOMPLETE_ONBOARDING: 'Incomplete Onboarding',
    EXPIRING_LICENSES: 'Expiring Licenses',
    HIGH_CANCEL_RATE: 'High Cancellation Rate',
};

const ALERT_DESC: Record<string, string> = {
    FAILED_CHARGES: 'These charges could not be processed because the patient\'s card on file was declined or expired.',
    PAST_DUE_INVOICES: 'These invoices have passed their due date and payment has not been collected.',
    INCOMPLETE_ONBOARDING: 'These team members have not yet completed all required onboarding steps.',
    EXPIRING_LICENSES: 'These provider licenses will expire within the next 30 days and need renewal.',
    HIGH_CANCEL_RATE: 'The average cancellation rate is significantly higher than your typical range.',
};

const ALERT_BADGE: Record<string, { color: 'red' | 'amber'; getText: (detail: TPracticeAttentionDetailVo) => string }> = {
    FAILED_CHARGES: {
        color: 'red',
        getText: (d) => `${d.failedCharges?.length ?? 0} failed`,
    },
    PAST_DUE_INVOICES: {
        color: 'red',
        getText: (d) => `${d.pastDueInvoices?.length ?? 0} past due`,
    },
    INCOMPLETE_ONBOARDING: {
        color: 'amber',
        getText: (d) => `${d.incompleteOnboarding?.length ?? 0} pending`,
    },
    EXPIRING_LICENSES: {
        color: 'amber',
        getText: (d) => `${d.expiringLicenses?.length ?? 0} expiring`,
    },
    HIGH_CANCEL_RATE: {
        color: 'amber',
        getText: (d) => d.highCancelRate ? `${d.highCancelRate.weekRatePct}% this week` : 'High rate',
    },
};

const formatCents = (cents: number): string => {
    return `$${(cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

const formatDate = (dateStr: string): string => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (dateStr: string): string => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
        ' · ' +
        d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const ROLE_LABELS: Record<string, string> = {
    GROUP_OWNER: 'Owner',
    GROUP_PROVIDER: 'Provider',
    GROUP_PRACTICE_MANAGER: 'Practice manager',
    GROUP_ADMINISTRATIVE_ASSISTANT: 'Administrative assistant',
    GROUP_CLINICAL_ASSISTANT: 'Clinical assistant',
};

const getInitials = (name: string): string => {
    return name
        .split(' ')
        .filter(Boolean)
        .map((w) => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
};

const FailedChargesDetail: React.FC<{ detail: TPracticeAttentionDetailVo }> = ({ detail }) => {
    const summary = detail.failedChargesSummary;
    const items = detail.failedCharges ?? [];

    return (
        <>
            {summary && (
                <div className={s.statRow}>
                    <div className={s.statItem}>
                        <div className={s.statLabel}>Total failed</div>
                        <div className={`${s.statValue} ${s.red}`}>{formatCents(summary.totalAmountCents)}</div>
                    </div>
                    <div className={s.statItem}>
                        <div className={s.statLabel}>Patients affected</div>
                        <div className={s.statValue}>{summary.patientsAffected}</div>
                    </div>
                    <div className={s.statItem}>
                        <div className={s.statLabel}>Avg days overdue</div>
                        <div className={s.statValue}>{summary.avgDaysOverdue}</div>
                    </div>
                </div>
            )}
            <div className={s.tableScroll}>
                <table className={s.detailTable}>
                    <thead>
                        <tr>
                            <th>Patient</th>
                            <th>Provider</th>
                            <th>Appointment</th>
                            <th>Date &amp; time</th>
                            <th className={s.right}>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => (
                            <tr key={`${item.source}-${item.id}`}>
                                <td>{item.patientName || '-'}</td>
                                <td>
                                    <div className={s.cellMain}>{item.providerName}</div>
                                    <div className={s.cellSub}>{item.providerCredential}</div>
                                </td>
                                <td className={s.wrap}>
                                    {item.serviceSpecialty
                                        ? `${item.serviceSpecialty}${item.serviceType ? `: ${item.serviceType}` : ''}`
                                        : '-'}
                                </td>
                                <td>{item.failedAt ? formatDateTime(item.failedAt) : '-'}</td>
                                <td className={s.right}>{formatCents(item.amountCents)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

const PastDueInvoicesDetail: React.FC<{ detail: TPracticeAttentionDetailVo }> = ({ detail }) => {
    const summary = detail.pastDueInvoicesSummary;
    const items = detail.pastDueInvoices ?? [];

    return (
        <>
            {summary && (
                <div className={s.statRow}>
                    <div className={s.statItem}>
                        <div className={s.statLabel}>Total past due</div>
                        <div className={`${s.statValue} ${s.red}`}>{formatCents(summary.totalAmountCents)}</div>
                    </div>
                    <div className={s.statItem}>
                        <div className={s.statLabel}>Invoices</div>
                        <div className={s.statValue}>{summary.invoiceCount}</div>
                    </div>
                    <div className={s.statItem}>
                        <div className={s.statLabel}>Oldest overdue</div>
                        <div className={s.statValue}>{summary.oldestOverdueDays} days</div>
                    </div>
                </div>
            )}
            <div className={s.tableScroll}>
                <table className={s.detailTable}>
                    <thead>
                        <tr>
                            <th>Patient</th>
                            <th>Provider</th>
                            <th>Charge type</th>
                            <th className={s.right}>Patient charge</th>
                            <th>Due date</th>
                            <th>Invoice</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => (
                            <tr key={item.invoiceId}>
                                <td>{item.patientName || '-'}</td>
                                <td>
                                    <div className={s.cellMain}>{item.providerName}</div>
                                    <div className={s.cellSub}>{item.providerCredential}</div>
                                </td>
                                <td className={s.wrap}>{item.chargeType || '-'}</td>
                                <td className={s.right}>{formatCents(item.amountCents)}</td>
                                <td>
                                    <span className={s.dueDateText}>{formatDate(item.dueDate)}</span>
                                </td>
                                <td>{item.invoiceNo}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

const IncompleteOnboardingDetail: React.FC<{ detail: TPracticeAttentionDetailVo }> = ({ detail }) => {
    const items = detail.incompleteOnboarding ?? [];

    return (
        <div className={s.obList}>
            {items.map((item) => (
                <div key={item.memberId} className={s.obCard}>
                    <div className={s.obTop}>
                        <div className={s.obAvatar}>{getInitials(item.memberName)}</div>
                        <div className={s.obInfo}>
                            <div className={s.obName}>
                                {item.memberName}
                                {item.isAdmin && <span className={s.adminBadge}>Admin</span>}
                            </div>
                            <div className={s.obSub}>
                                {ROLE_LABELS[item.roleTitle] || item.roleTitle} · Joined {item.daysSinceJoined} days ago
                            </div>
                        </div>
                    </div>
                    <div className={s.obProgress}>
                        <div className={s.obProgressBar}>
                            <div
                                className={s.obProgressFill}
                                style={{ width: item.currentStep ? '40%' : '0%' }}
                            />
                        </div>
                        <span className={s.obStatus}>{item.portalStatus}</span>
                    </div>
                </div>
            ))}
        </div>
    );
};

const ExpiringLicensesDetail: React.FC<{ detail: TPracticeAttentionDetailVo }> = ({ detail }) => {
    const items = detail.expiringLicenses ?? [];

    return (
        <div className={s.tableScroll}>
            <table className={s.detailTable}>
                <thead>
                    <tr>
                        <th>Provider</th>
                        <th>License type</th>
                        <th>State</th>
                        <th>Expires</th>
                        <th>Days left</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item) => (
                        <tr key={item.licenseId}>
                            <td>
                                <div className={s.cellMain}>{item.providerName}</div>
                                <div className={s.cellSub}>{item.providerCredential}</div>
                            </td>
                            <td>{item.licenseType || '-'}</td>
                            <td>{item.state || '-'}</td>
                            <td>{formatDate(item.expireDate)}</td>
                            <td>
                                <span className={item.daysUntilExpiry <= 14 ? s.statusFailed : s.statusWarning}>
                                    {item.daysUntilExpiry} days
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const HighCancelRateDetail: React.FC<{ detail: TPracticeAttentionDetailVo }> = ({ detail }) => {
    const data = detail.highCancelRate;
    if (!data) return <div className={s.detailEmpty}>No details available</div>;

    const providers = data.providerBreakdown ?? [];

    return (
        <>
            <div className={s.statRow}>
                <div className={s.statItem}>
                    <div className={s.statLabel}>This week</div>
                    <div className={`${s.statValue} ${s.amber}`}>{data.weekRatePct}%</div>
                </div>
                <div className={s.statItem}>
                    <div className={s.statLabel}>Your typical</div>
                    <div className={s.statValue}>{data.baselineRatePct}%</div>
                </div>
                <div className={s.statItem}>
                    <div className={s.statLabel}>Cancellations</div>
                    <div className={s.statValue}>{data.weekCanceled}</div>
                </div>
            </div>
            <div className={s.tableScroll}>
                <table className={s.detailTable}>
                    <thead>
                        <tr>
                            <th>Provider</th>
                            <th className={s.right}>Cancellations</th>
                            <th className={s.right}>Rate</th>
                            <th>Top reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        {providers.map((item) => (
                            <tr key={item.providerId}>
                                <td>
                                    <div className={s.cellMain}>{item.providerName}</div>
                                    <div className={s.cellSub}>{item.providerCredential}</div>
                                </td>
                                <td className={s.right}>{item.cancellations}</td>
                                <td className={s.right}>
                                    <span style={{ color: item.ratePct >= 15 ? '#991b1b' : '#92400e', fontWeight: 500 }}>
                                        {item.ratePct}%
                                    </span>
                                </td>
                                <td>{item.topReason}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

const DetailContent: React.FC<{ alertType: string; detail: TPracticeAttentionDetailVo }> = ({ alertType, detail }) => {
    switch (alertType) {
        case 'FAILED_CHARGES':
            return <FailedChargesDetail detail={detail} />;
        case 'PAST_DUE_INVOICES':
            return <PastDueInvoicesDetail detail={detail} />;
        case 'INCOMPLETE_ONBOARDING':
            return <IncompleteOnboardingDetail detail={detail} />;
        case 'EXPIRING_LICENSES':
            return <ExpiringLicensesDetail detail={detail} />;
        case 'HIGH_CANCEL_RATE':
            return <HighCancelRateDetail detail={detail} />;
        default:
            return <div className={s.detailEmpty}>No details available</div>;
    }
};

const AttentionNeeded: React.FC = () => {
    const [items, setItems] = useState<TPracticeAttentionAlertVo[]>([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detail, setDetail] = useState<TPracticeAttentionDetailVo | null>(null);
    const [activeAlertType, setActiveAlertType] = useState<string>('');

    useEffect(() => {
        getPracticeOverviewAttention().then((res) => {
            if (!res.error) setItems(res?.data?.data ?? []);
        });
    }, []);

    const handleDismiss = useCallback(async (alertType: TAttentionAlertType) => {
        const res = await dismissPracticeOverviewAttention(alertType);
        if (!res.error) {
            setItems((prev) => prev.filter((a) => a.alertType !== alertType));
        }
    }, []);

    const handleSeeDetails = useCallback(async (alertType: string) => {
        setActiveAlertType(alertType);
        setDrawerOpen(true);
        setDetailLoading(true);
        setDetail(null);
        try {
            const res = await getPracticeOverviewAttentionDetail(alertType as TAttentionAlertType);
            if (!res.error) {
                setDetail(res?.data?.data ?? null);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setDetailLoading(false);
        }
    }, []);

    const visibleItems = items.filter((item) => !item.dismissed);
    if (visibleItems.length === 0) return null;

    const getAlertText = (item: TPracticeAttentionAlertVo): string => {
        const template = ALERT_LABELS[item.alertType];
        if (!template) return `${item.count} alert(s) require attention.`;
        if (item.alertType === 'HIGH_CANCEL_RATE') return template;
        return `${item.count} ${template}`;
    };

    const badgeConfig = ALERT_BADGE[activeAlertType];

    return (
        <div>
            <h2 className={s.title}>Attention Needed</h2>
            <div className={s.card}>
                {visibleItems.map((item) => (
                    <div key={item.alertType} className={s.row}>
                        <span className={s.icon}>
                            {item.severity === 'CRITICAL' ? <ErrorIcon /> : <WarningIcon />}
                        </span>
                        <span className={s.text}>{getAlertText(item)}</span>
                        <a
                            className={s.link}
                            onClick={() => handleSeeDetails(item.alertType)}
                        >
                            See details
                        </a>
                        <Button
                            className={s.dismissBtn}
                            onClick={() => handleDismiss(item.alertType as TAttentionAlertType)}
                        >
                            <svg
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                width="16"
                                height="16"
                            >
                                <line x1="6" y1="6" x2="14" y2="14" />
                                <line x1="14" y1="6" x2="6" y2="14" />
                            </svg>
                        </Button>
                    </div>
                ))}
            </div>

            <Drawer
                title={
                    <div className={s.drawerTitle}>
                        <span>{ALERT_TITLE[activeAlertType] ?? 'Alert Details'}</span>
                        {detail && badgeConfig && (
                            <span className={`${s.badge} ${s[badgeConfig.color]}`}>
                                {badgeConfig.getText(detail)}
                            </span>
                        )}
                    </div>
                }
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                width="60vw"
                destroyOnClose
            >
                {detailLoading ? (
                    <div className={s.detailLoading}><Spin /></div>
                ) : detail ? (
                    <div>
                        <p className={s.panelDesc}>{ALERT_DESC[activeAlertType]}</p>
                        <DetailContent alertType={activeAlertType} detail={detail} />
                    </div>
                ) : (
                    <div className={s.detailEmpty}>No details available</div>
                )}
            </Drawer>
        </div>
    );
};

export default AttentionNeeded;
